import { Fragment, forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Breadcrumb.css";

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  current?: boolean;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: ReactNode;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const DefaultSep = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(function Breadcrumb(
  { items, separator, className, ...rest },
  ref,
) {
  const sep = separator ?? <DefaultSep />;
  return (
    <nav ref={ref} aria-label="Breadcrumb" className={cx("go4-breadcrumb", className)} {...rest}>
      <ol className="go4-breadcrumb__list">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          const current = item.current ?? isLast;
          return (
            <Fragment key={idx}>
              <li className="go4-breadcrumb__item">
                {current ? (
                  <span aria-current="page" className="go4-breadcrumb__current">
                    {item.label}
                  </span>
                ) : item.href ? (
                  <a href={item.href} onClick={item.onClick} className="go4-breadcrumb__link">
                    {item.label}
                  </a>
                ) : (
                  <button type="button" onClick={item.onClick} className="go4-breadcrumb__link">
                    {item.label}
                  </button>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="go4-breadcrumb__separator">
                  {sep}
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
});
